import { ReactNode } from 'react';
import useTranslation from 'next-translate/useTranslation';
import {
  EXPLANATION_DIRECTION,
  SuggestedPriceExplanationFactor,
} from '@features/suggested-price-column/api/get-suggested-price-explanation';
import { SuggestedPriceCategoryIcon } from '@features/suggested-price-column/components/suggested-price-category-icon/suggested-price-category-icon';
import { SuggestedPriceDetailRow } from '@features/suggested-price-column/components/suggested-price-detail-row/suggested-price-detail-row';
import { applyFactorAmount } from '@features/suggested-price-column/utils/apply-factor-amount';
import { groupFactorsByCategory } from '@features/suggested-price-column/utils/group-factors-by-category';

import styles from './suggested-price-factor-groups.module.scss';

interface SuggestedPriceFactorGroupsProps {
  currency: string;
  factors: SuggestedPriceExplanationFactor[];
  pricePrecision: number;
}

/**
 * A category's reasons as a row description: bulleted once there are several, and left a plain
 * sentence when there is only one so the common single-reason row reads as it always has.
 */
const renderReasons = (reasons: string[]): ReactNode => {
  if (reasons.length === 1) return reasons[0];

  return (
    <ul className={styles.reasons}>
      {reasons.map(reason => (
        <li key={reason} className={styles.reason}>{reason}</li>
      ))}
    </ul>
  );
};

/**
 * Renders the explanation factors split into the two directional groups the design calls for.
 *
 * A positive SHAP value means the feature pushed toward a *deeper* discount, which *lowers* the
 * suggested price — so POSITIVE belongs under the "lowered" heading, not the "raised" one. This
 * is verified against the copy spreadsheet's worked examples (shap +1.49 renders as "a deeper
 * discount here") and is deliberately not the intuitive reading.
 *
 * A group with no factors is omitted entirely rather than rendered with an empty body. An empty
 * factor list renders nothing — the drawer shows an informational notice instead.
 *
 * Within a heading the factors are folded to one row per category, since several factors can
 * share a category and would otherwise repeat its heading.
 */
export const SuggestedPriceFactorGroups = ({
  currency,
  factors,
  pricePrecision,
}: SuggestedPriceFactorGroupsProps) => {
  const { t } = useTranslation('common');

  const lowered = factors.filter(factor => factor.direction === EXPLANATION_DIRECTION.POSITIVE);
  const raised = factors.filter(factor => factor.direction === EXPLANATION_DIRECTION.NEGATIVE);

  const resolveCopy = (factor: SuggestedPriceExplanationFactor) => applyFactorAmount({
    amount: factor.amount, copy: factor.copy, currency, pricePrecision,
  });

  const groups = [
    { factors: lowered, key: 'lowered', label: t('bid_request.line_items.suggested_price.explanation.lowered_heading') },
    { factors: raised, key: 'raised', label: t('bid_request.line_items.suggested_price.explanation.raised_heading') },
  ];

  return (
    <>
      {groups.filter(group => group.factors.length > 0).map(group => (
        <section key={group.key} className={styles.group} data-testid={`suggested-price-factors-${group.key}`}>
          <h4 className={styles.heading}>{group.label}</h4>
          {groupFactorsByCategory(group.factors, resolveCopy).map(category => (
            <SuggestedPriceDetailRow
              key={category.categoryKey}
              dataTestId={`suggested-price-factor-${category.categoryKey}`}
              description={renderReasons(category.reasons)}
              icon={<SuggestedPriceCategoryIcon category={category.categoryKey} />}
              title={category.categoryLabel}
            />
          ))}
        </section>
      ))}
    </>
  );
};
